export interface CsvExportMetadata {
  store: string;
  macroplanogram: string;
}

export interface CsvExportOptions {
  /** Header label for the scope column (e.g. "Subcategory" for WFM).
   *  Defaults to "Subcategory". Only used when `includeScopeColumn` is true. */
  scopeColumnLabel?: string;
  /** Header label for the item-id column (e.g. "Scancode" for WFM,
   *  "ASIN" for GFC). Defaults to "Scancode". */
  itemIdLabel?: string;
  /** Whether to include the scope (subcategory) column. GFC Drop Zone is a
   *  single fixed scope, so the column is omitted. Defaults to true. */
  includeScopeColumn?: boolean;
}

export interface CsvRow {
  scancode: string;
  title: string;
  imageLink: string;
  /** Value for the scope column (subcategoryName). Ignored when the export
   *  omits the scope column. */
  scope: string;
  startTime: string;
  endTime: string;
}

const FORMULA_PREFIXES = /^[=+\-@\t\r]/;

/**
 * Escapes a CSV field per RFC 4180 with formula-injection protection.
 * Prefixes a single quote to neutralise spreadsheet formula injection when
 * the value starts with =, +, -, @, \t, or \r.
 * Wraps in double quotes if the value contains a comma, double quote, newline, or carriage return.
 * Doubles any existing double quotes within the value.
 */
export const escapeCsvField = (value: string): string => {
  let safe = value;
  if (FORMULA_PREFIXES.test(safe)) {
    safe = `'${safe}`;
  }
  if (safe.includes(',') || safe.includes('"') || safe.includes('\n') || safe.includes('\r')) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

/**
 * Generates a CSV string from rows and metadata.
 * Produces a header row followed by one escaped data row per entry.
 */
export const generateCsv = (rows: CsvRow[], metadata: CsvExportMetadata, options: CsvExportOptions = {}): string => {
  const scopeLabel = options.scopeColumnLabel ?? 'Subcategory';
  const itemIdLabel = options.itemIdLabel ?? 'Scancode';
  const includeScopeColumn = options.includeScopeColumn ?? true;

  const headerCols = [
    'Store',
    'Macroplanogram',
    ...(includeScopeColumn ? [scopeLabel] : []),
    itemIdLabel,
    'Title',
    'Image Link',
    'Start Time',
    'End Time',
  ];
  const header = headerCols.join(',');

  const dataRows = rows.map((row) => {
    const fields = [
      metadata.store,
      metadata.macroplanogram,
      ...(includeScopeColumn ? [row.scope] : []),
      row.scancode,
      row.title,
      row.imageLink,
      row.startTime,
      row.endTime,
    ];
    return fields.map(escapeCsvField).join(',');
  });

  return [header, ...dataRows].join('\n');
};

/**
 * Builds a CSV file name from store, department, and date.
 * Replaces whitespace with underscores.
 */
export const buildCsvFileName = (store: string, department: string, date: string): string => {
  const sanitize = (s: string) => s.replace(/[/\\:*?"<>|\s]/g, '_');
  return `${sanitize(store)}_${sanitize(department)}_${sanitize(date)}.csv`;
};

/**
 * Triggers a browser download of CSV content as a file.
 * Creates a Blob, triggers download via temporary anchor element, and revokes the object URL.
 */
export const downloadCsv = (csvContent: string, fileName: string): void => {
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
